import * as React from 'react';

import { ProductTileModel } from '@notino/react-styleguide';
import { IStock } from '@notino/shared/definitions/custom-definitions';
import { CatalogVariantFragmentFragment } from '@notino/shared/definitions/types';

import PriceLabel from '@components/PriceLabel';
import { Tags } from '@components/Tags';
import { getSupportedAttributes } from '@components/Tags/model';
import { ILocale } from '@containers/App/model';
import { useIsDualPrice } from '@containers/ProductDetailContainer/ProductDetail/hooks/useIsDualPrice';
import { getVariantName } from '@containers/ProductDetailContainer/ProductDetail/utils';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { VariantDisplayTypes } from '@containers/ProductListing/model';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { variantInfo, dataLabelsProp } from '@helpers/utils';

import { usePriceWithVoucher } from '../../hooks/usePriceWithVoucher';
import { trackVariantClick } from '../../tracking';
import { Unavailable } from '../../Variants/Unavailable';
import { HandleTransitionClick } from '../../Variants/useVariantTransition';

import {
  Row,
  ImgWrapper,
  Price,
  PriceBesideBelow,
  PriceWrapper,
  PriceSymbol,
  Name,
  Img,
  DualPriceWrapper,
  Slash,
} from './styled';
import { VariantDiscount } from './VariantDiscount';

interface IVariantItemProps {
  variant: CatalogVariantFragmentFragment;
  locale: ILocale;
  isSelected: boolean;
  onClick?: HandleTransitionClick;
}

export const VariantItem: React.FC<IVariantItemProps> = ({
  variant,
  locale,
  isSelected,
  onClick,
}) => {
  const price = usePriceWithVoucher(variant);
  const { getTimeFromInit } = useTrackingContext();
  const supportedAttributes = getSupportedAttributes();
  const {
    isDualPrice,
    getRightSidePrice,
    getLeftSideFormattedPrice,
    leftSideCurrency,
    rightSideCurrency,
  } = useIsDualPrice();
  const { showGroups } = useProductDetailContext().variantGroups;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    onClick?.(variant, e);
    trackVariantClick({
      timing: getTimeFromInit(),
      type: VariantDisplayTypes.Select,
      variantName: getVariantName(variant) ?? '',
    });
  };

  const inStock = variant.stockAvailability.code !== IStock.outOfStock;

  return (
    <Row
      to={variant.url ?? ''}
      replace={true}
      isSelected={isSelected}
      notInStock={!inStock}
      id={`pd-variant-${variant.webId}`}
      data-testid={`pd-variant-${variant.webId}`}
      onClick={handleClick}
      {...dataLabelsProp({
        attributes: variant.attributes,
        supportedAttributes,
      })}
    >
      <ImgWrapper>
        <Img
          src={variant.media?.mainImage?.src ?? ''}
          alt={variant.media?.mainImage?.alt ?? undefined}
          sourceKey="thumb"
        />
      </ImgWrapper>

      <PriceBesideBelow>
        <Name>
          <Tags
            small={true}
            multiple={true}
            attributes={variant.attributes}
            stockAvailability={variant.stockAvailability.code ?? ''}
            customFilter={(attr) =>
              attr !== ProductTileModel.ProductLabelAttributes.VirtualTryOn
            }
          />
          <span className="pd-variant-label">
            {variantInfo(
              variant as Parameters<typeof variantInfo>[0],
              showGroups
            )}
          </span>
        </Name>

        {!inStock && <Unavailable />}

        {inStock && (
          <PriceWrapper>
            <DualPriceWrapper>
              {isDualPrice && (
                <>
                  <Price isSelected={isSelected}>
                    {getLeftSideFormattedPrice(price.value ?? 0)}
                    <PriceSymbol>{leftSideCurrency}</PriceSymbol>
                  </Price>
                  <Slash>/</Slash>
                </>
              )}

              <PriceLabel
                price={
                  isDualPrice
                    ? {
                        value: getRightSidePrice(price.value ?? 0),
                      }
                    : {
                        value: price.value ?? 0,
                        currency: price.currency ?? undefined,
                      }
                }
                wrapperComponent={(props) => (
                  <Price isSelected={isSelected} {...props} />
                )}
                currencyComponent={PriceSymbol}
                currencySymbolOverride={
                  isDualPrice ? rightSideCurrency : undefined
                }
              />
            </DualPriceWrapper>

            <VariantDiscount variant={variant} locale={locale} />
          </PriceWrapper>
        )}
      </PriceBesideBelow>
    </Row>
  );
};
